import { Slot } from '@radix-ui/react-slot';
import React, {
  cloneElement,
  isValidElement,
  ReactElement,
  MouseEvent
} from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';

/**
 * Custom NavLink wrapper that passes isActive to its child via cloneElement
 *
 * Uses useRouteMatch to determine active state and manually handles navigation.
 * Clones the child element and injects the `isActive` prop automatically.
 *
 * @example
 * ```tsx
 * <NavLink to="/dashboard">
 *   <MenuItem icon={<Home />}>Dashboard</MenuItem>
 * </NavLink>
 * ```
 *
 * The MenuItem component will receive `isActive` as a prop automatically.
 */
export interface NavLinkProps
  extends React.AnchorHTMLAttributes<HTMLAnchorElement> {
  to: string;
  children: ReactElement;
  exact?: boolean;
  isActiveOverride?: boolean;
}

export const NavLink = React.forwardRef<HTMLAnchorElement, NavLinkProps>(
  (
    { children, to, exact = false, onClick, isActiveOverride, ...props },
    ref
  ) => {
    const history = useHistory();
    const match = useRouteMatch({ path: to, exact });
    const isActive = isActiveOverride ?? !!match;

    const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
      // Allow custom onClick handler if provided
      onClick?.(e);

      // Check for modifier keys or middle mouse button
      // Let the browser handle these cases (new tab/window)
      const isModifiedClick =
        e.button !== 0 || // middle/right mouse button
        e.metaKey || // cmd on mac
        e.ctrlKey || // ctrl on windows/linux
        e.shiftKey || // shift (new window)
        e.altKey; // alt

      // Only prevent default and use history.push for normal left clicks
      if (!e.defaultPrevented && !isModifiedClick) {
        e.preventDefault();
        history.push(to);
      }
    };

    if (!isValidElement(children)) {
      return children;
    }

    // Clone the child element and inject isActive prop
    const childWithActive = cloneElement(children, { isActive } as any);

    return (
      <Slot
        {...props}
        // @ts-expect-error this is a element
        href={to}
        ref={ref}
        onClick={handleClick}
      >
        {childWithActive}
      </Slot>
    );
  }
);
